// src/services/authService.js
import axios from 'axios';
import { initTokenManager } from './tokenService';

// Configuración de las URLs de la API
const API_URLS = {
    production: 'https://centro-infantil-mijardin.onrender.com/api',
    development: 'http://localhost:5000/api'
};

// Determinar la URL base según el entorno
const API_URL = process.env.NODE_ENV === 'production' ? API_URLS.production : API_URLS.development;

// Configuración global de axios
axios.defaults.withCredentials = true;

// Función para iniciar sesión
export const login = async (username, password) => {
    try {
        const response = await axios.post(`${API_URL}/auth/login`, { username, password });
        
        if (response.data.token) {
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('user', JSON.stringify(response.data.user));
            localStorage.setItem('passwordIsCedula', response.data.passwordIsCedula);
            
            // Configurar el token para futuras peticiones
            axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
            
            // Inicializar el gestor de tokens para renovación automática y detección de inactividad
            initTokenManager();
            
            return {
                token: response.data.token,
                user: response.data.user,
                passwordIsCedula: response.data.passwordIsCedula
            };
        } else {
            throw new Error('No se recibió el token de autenticación');
        }
    } catch (error) {
        if (error.response?.data?.message) {
            throw new Error(error.response.data.message);
        }
        
        switch (error.response?.status) {
            case 403:
                throw new Error('Usuario desactivado. Contacte al administrador.');
            case 401:
                throw new Error('Credenciales incorrectas');
            case 0:
                throw new Error('Error de conexión con el servidor');
            default:
                throw new Error('Error al iniciar sesión');
        }
    }
};

// Función para cerrar sesión
export const logout = async () => {
  try {
    const token = localStorage.getItem('token');

    if (token) {
      const response = await axios.post(`${API_URL}/auth/logout`, {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        // Usar el servicio de token para limpiar la sesión
        import('./tokenService').then(({ logout }) => {
          logout('Has cerrado sesión correctamente.');
        });
      }
    }
  } catch (error) {
    // Incluso si hay un error, intentar limpiar la sesión
    import('./tokenService').then(({ logout }) => {
      logout('Has cerrado sesión.');
    });
  }
};

// Función para recuperar la contraseña
export const requestPasswordReset = async (email) => {
  try {
      const response = await axios.post(`${API_URL}/password/forgot-password`, { email });
      return response.data;
  } catch (error) {
      throw new Error(error.response?.data?.message || 'Error al solicitar la recuperación de contraseña');
  }
};

// Función para restablecer la contraseña
export const resetPassword = async (token, newPassword) => {
  try {
      const response = await axios.post(`${API_URL}/password/reset-password`, { token, newPassword });
      return response.data;
  } catch (error) {
      throw new Error(error.response?.data?.message || 'Error al restablecer la contraseña');
  }
};

export const registerProfessional = async (userData) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No hay sesión activa. Por favor inicia sesión nuevamente.');
    }

    const response = await axios.post(
      `${API_URL}/auth/admin/register-professional`,
      {
        nombre: userData.name,
        email: userData.email,
        cedula: userData.cedula,
        password_us: userData.cedula, // La contraseña será la cédula
        rol: userData.rol,
        estado: 'Activa' // Estado por defecto al registrar
      },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error('Ha ocurrido un error con tu sesión. Por favor vuelve a iniciar sesión.');
    }
    throw new Error('Error al registrar usuario: ' + (error.response?.data?.message || error.message));
  }
};

export const UpdateUsers = async (id, name, email, rol, token, cedula, estado) => {
  try {
    const response = await axios.put(
      `${API_URL}/auth/admin/update-user/${id}`, 
      { name, email, rol, cedula, estado }, 
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error) {
    if (error.response?.data?.error === 'EMAIL_EXISTS') {
      const err = new Error(error.response.data.message || 'El correo electrónico ya está en uso.');
      err.code = 'EMAIL_EXISTS';
      throw err;
    } else if (error.response?.data?.error === 'CEDULA_EXISTS') {
      const err = new Error(error.response.data.message || 'La cédula ya está registrada.');
      err.code = 'CEDULA_EXISTS';
      throw err;
    }
    throw new Error('Error al actualizar usuario: ' + (error.response?.data?.message || error.message));
  }
};

export const GetUsers = async (token) => {
  try {
    const response = await axios.get(`${API_URL}/auth/admin/users`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error('Error al obtener usuarios: ' + error.response?.data?.message || error.message);
  }
};

export const deleteUser = async (id, token) => {
  try {
    const response = await axios.delete(
      `${API_URL}/auth/admin/delete-user/${id}`, 
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          id: id
        }
      }
    );
    return response.data;
  } catch (error) {
    throw new Error('Error al eliminar usuario: ' + error.response?.data?.message || error.message);
  }
};

// Nuevo método para actualizar el estado del usuario
export const updateStatusUser = async (id, estado) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No hay sesión activa. Por favor inicia sesión nuevamente.');
    }

    const response = await axios.put(
      `${API_URL}/auth/admin/update-user-status/${id}`,
      { estado },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error('Ha ocurrido un error con tu sesión. Por favor vuelve a iniciar sesión.');
    }
    throw new Error('Error al actualizar el estado del usuario: ' + (error.response?.data?.message || error.message));
  }
};



